import { useEffect } from 'react';
import { createRootRoute, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { UpdateRequiredScreen, useAppUpgradeRequired, useAppUpgradeResolution } from '@tupai/shell';
import { getAppMeta } from '@/lib/api';
import { useTranslation } from '@tupai/i18n/react';
import 'katex/dist/katex.min.css';
import { updateApp } from '@/lib/ota-updater';

const resolveUpdate = (minimum: string) =>
  updateApp({ app: 'parent', mode: 'required', minAppVersion: minimum });

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  const updateRequired = useAppUpgradeRequired();
  const { status, retry } = useAppUpgradeResolution(resolveUpdate);
  const { t } = useTranslation('common');
  useEffect(() => {
    getAppMeta().catch(() => {});
    // launchAutoHide is off (capacitor.config.ts); hidden here so a crash or redirect can't strand the app on the splash.
    if (Capacitor.isNativePlatform()) SplashScreen.hide().catch(() => {});
  }, []);

  if (updateRequired) {
    const platform = Capacitor.getPlatform();
    const iosId = import.meta.env.VITE_PARENT_IOS_APP_STORE_ID;
    const storeUrl =
      platform === 'android'
        ? 'https://play.google.com/store/apps/details?id=ai.tupai.parent'
        : platform === 'ios' && iosId
          ? `https://apps.apple.com/app/id${iosId}`
          : undefined;
    return (
      <UpdateRequiredScreen
        storeUrl={status === 'store' ? storeUrl : undefined}
        title={t('updateRequired')}
        message={t(
          status === 'checking'
            ? 'updateDownloading'
            : status === 'error'
              ? 'updateDownloadFailed'
              : 'updateRequiredBody'
        )}
        updateLabel={t('updateNow')}
        onRetry={status === 'error' ? retry : undefined}
        retryLabel={t('updateRetry')}
      />
    );
  }
  return (
    <>
      <Outlet />
      {import.meta.env.MODE === 'development' && <TanStackRouterDevtools />}
    </>
  );
}
